import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Button, Divider, Flex, Text } from 'core/components';
import Page from 'app/components/page/Page';
import storeLoader from 'app/stores/storeLoader';
import { TbLayoutSidebarRightCollapse, TbLayoutSidebarRightExpand } from 'react-icons/tb';
import KaizenMainContent from './KaizenMainContent';
import TokenUsageDialog from './components/TokenUsageDialog';
import SessionFeatureSelector from './components/SessionFeatureSelector';
import McpStatusIndicator from './components/McpStatusIndicator';
import SessionFeatureIndicator from './components/SessionFeatureIndicator';

const SIDEBAR_OPEN_STORAGE_KEY = 'kaizenSidebarOpen';

@storeLoader('$users')
@inject('$auth', '$companySettings', '$kaizen')
@withRouter
@observer
class KaizenMain extends Component {
  state = {
    isMyJourneysOpen: true,
    isCompanyJourneysOpen: true,
    isScrolledToBottom: false,
    hasChatList: false,
    selectedToolResults: [],
    isExistingChatLoading: false,
    searchInputValue: '',
    isTokenUsageOpen: false,
    isSidebarOpen: localStorage.getItem(SIDEBAR_OPEN_STORAGE_KEY) !== 'false'
  };

  inputRef = React.createRef();

  scrollAreaRef = React.createRef();

  componentDidMount() {
    const { match, $kaizen } = this.props;
    const { sessionId } = match.params;

    if (this.hasAi) {
      $kaizen.subscribeToHeartbeats();

      // Don't wait on the (company-wide) chat list -- open the requested chat right away
      if (sessionId) {
        this.openSession(sessionId);
      }

      $kaizen.chatSessionCollection
        .fetch({ preserveSelection: !!sessionId })
        .then(() => !this.isUnmounted && this.setState({ hasChatList: true }))
        .catch(() => {});
    }
  }

  openSession(sessionId) {
    const { $kaizen } = this.props;
    const { hasChatList } = this.state;
    const { chatSessionCollection } = $kaizen;
    let journey = chatSessionCollection.get(sessionId);
    let isForged = false;

    if (!journey) {
      if (hasChatList) {
        this.handleSessionNotFound(sessionId);
        return;
      }

      // the chat list is still loading; open the session directly. user_id is filled in by the session fetch,
      // leaving it empty keeps someone else's chat from flashing in the current user's recent list.
      journey = chatSessionCollection.forge({ id: sessionId, user_id: null }, { select: false });
      chatSessionCollection.add(journey);
      isForged = true;
    }

    const request = this.handleSelectJourney(journey);

    if (isForged) {
      // opened before the chat list loaded, so the session fetch is what tells us it doesn't exist
      request.catch(() => {
        chatSessionCollection.remove(sessionId);
        this.handleSessionNotFound(sessionId);
      });
    }
  }

  handleSessionNotFound = (sessionId) => {
    const { history, match, $kaizen } = this.props;

    // only redirect if the user is still looking at the missing session
    if (!this.isUnmounted && match.params.sessionId === sessionId) {
      history.push($kaizen.baseUrl);
    }
  };

  handleEmptyStart = () => {
    const { history, $kaizen } = this.props;
    $kaizen.chatSessionCollection?.unselectAll();
    $kaizen.clearSessionFeatures();
    this.setState({
      selectedToolResults: [],
      isExistingChatLoading: false
    });
    history.push($kaizen.baseUrl);
  };

  componentWillUnmount() {
    const { $kaizen } = this.props;
    this.isUnmounted = true;

    // Remove window resize listener
    window.removeEventListener('resize', this.handleResize);

    $kaizen.unsubscribeFromHeartbeats();
    $kaizen.chatSessionCollection.models.forEach((model) => model.unsubscribe());
    $kaizen.chatSessionCollection.clearFilters();
    $kaizen.chatSessionCollection.clearSelection();
  }

  componentDidUpdate(prevProps) {
    const { match } = this.props;
    const { match: prevMatch } = prevProps;

    if (match.params.sessionId && match.params.sessionId !== prevMatch.params.sessionId) {
      this.openSession(match.params.sessionId);
    }
  }

  handleScroll = () => {
    const { scrollHeight, scrollTop } = this.scrollAreaRef.current;
    const scrollAreaHeight = this.scrollAreaRef.current.clientHeight;

    //  Math.abs(... <= 1) → Checks if the difference is very small (≤ 1px) to account for rounding errors in different browsers.
    const isBottom = Math.abs(scrollHeight - Math.ceil(scrollTop) - scrollAreaHeight) <= 1;
    this.setState({ isScrolledToBottom: isBottom });
  };

  scrollToBottom = () => {
    if (this.scrollAreaRef?.current) {
      setTimeout(() => {
        const offsetBottom = this.scrollAreaRef.current.scrollTop + this.scrollAreaRef.current.scrollHeight + 100;
        this.scrollAreaRef.current.scrollTo({ top: offsetBottom, behavior: 'smooth' });
      }, 350);
    }
  };

  handleCreateJourney = () => {
    const { $kaizen } = this.props;
    const newJourney = $kaizen.createJourney();
    this.setState({ isExistingChatLoading: false });
    return newJourney;
  };

  handleSelectJourneyAfterCreation = (journey) => {
    const { history, $kaizen } = this.props;
    history.push(`${$kaizen.baseUrl}/${journey.id}`);
  };

  handleToggleMyJourneys = () => {
    this.setState((prevState) => {
      const { isMyJourneysOpen } = prevState;
      return { isMyJourneysOpen: !isMyJourneysOpen };
    });
  };

  handleToggleCompanyJourneys = () => {
    this.setState((prevState) => {
      const { isCompanyJourneysOpen } = prevState;
      return { isCompanyJourneysOpen: !isCompanyJourneysOpen };
    });
  };

  handleSearchInputChange = (e) => {
    const { $kaizen } = this.props;
    const { value = '' } = e.target;
    this.setState({ searchInputValue: value });
    $kaizen.chatSessionCollection.searchChats(value);
  };

  handleSelectJourney = (journey) => {
    const { history, $kaizen, location, match } = this.props;
    const isSelectingCurrentSession = match?.params?.sessionId === journey.id;
    const hashSuffix = isSelectingCurrentSession ? location?.hash || '' : '';

    this.setState({
      selectedToolResults: [],
      isExistingChatLoading: true
    });
    history.push(`${$kaizen.baseUrl}/${journey.id}${hashSuffix}`);
    return journey.selectAndSubscribe({ fetch: true });
  };

  handleCloseToolResults = () => {
    this.setState({ selectedToolResults: [] });
  };

  handleExportPdf = () => {
    const { $kaizen, match } = this.props;
    const { sessionId } = match.params;
    if (sessionId) {
      $kaizen.exportPdf(sessionId);
    }
  };

  handleToggleSidebar = () => {
    this.setState(({ isSidebarOpen }) => {
      localStorage.setItem(SIDEBAR_OPEN_STORAGE_KEY, !isSidebarOpen);
      return { isSidebarOpen: !isSidebarOpen };
    });
  };

  handleOpenTokenUsage = () => this.setState({ isTokenUsageOpen: true });

  handleCloseTokenUsage = () => this.setState({ isTokenUsageOpen: false });

  handleViewPrompt() {
    window.open('/api/ui/kaizen/context', '_blank');
  }

  handleCloseToolResult = (tool) => {
    this.setState((prevState) => {
      const selectedToolResults = prevState.selectedToolResults.filter((t) => t.id !== tool.id);
      return { selectedToolResults };
    });
  };

  // The panel shows one result at a time — selecting replaces whatever is open.
  // The sidebar is page navigation, so it stays as the user left it.
  handleSelectToolResult = (tool) => {
    this.setState((prevState) => {
      const isAlreadyOpen =
        prevState.selectedToolResults.length === 1 && prevState.selectedToolResults[0].id === tool.id;
      return isAlreadyOpen ? null : { selectedToolResults: [tool] };
    });
  };

  get hasAi() {
    const { $companySettings, $auth } = this.props;
    return !!$companySettings.enableKentikAi && $auth.hasPermission('kaizen', { overrideForSudo: false });
  }

  renderSubnavTools = () => {
    const { $companySettings, $auth, match, $kaizen } = this.props;
    const { selectedToolResults, isSidebarOpen } = this.state;
    const hasSession = !!match?.params?.sessionId;

    if (!this.hasAi) {
      return null;
    }

    return (
      <Flex gap={1}>
        {$companySettings.demobox.enabled && (
          <>
            <Button minimal icon="search-template" text="Start Demo" onClick={this.handleStartInvestigation} mr={1} />
          </>
        )}
        {selectedToolResults?.length > 0 && (
          <>
            <Button icon="cross" text="Close Results" minimal onClick={this.handleCloseToolResults} />
            <Divider mx={1} />
          </>
        )}
        {hasSession && (
          <Button minimal icon="export" onClick={this.handleExportPdf} title="Export conversation as PDF">
            <Text>Export PDF</Text>
          </Button>
        )}
        {hasSession && $auth.isSudoer && (
          <Button minimal icon="chart" onClick={this.handleOpenTokenUsage} title="View token usage analytics">
            <Text>Token Usage</Text>
          </Button>
        )}
        <McpStatusIndicator />
        {!hasSession && $auth.isSudoer && (
          <SessionFeatureSelector
            selectedFeatures={$kaizen.selectedSessionFeatures}
            onToggleFeature={$kaizen.toggleSessionFeature}
          />
        )}
        {hasSession && (
          <SessionFeatureIndicator
            activeFeatures={$kaizen.chatSessionCollection.get(match.params.sessionId)?.get('session_features')}
          />
        )}
        <Button
          minimal
          icon={isSidebarOpen ? TbLayoutSidebarRightCollapse : TbLayoutSidebarRightExpand}
          text={isSidebarOpen ? 'Hide Sidebar' : 'Show Sidebar'}
          active={isSidebarOpen}
          ariaExpanded={isSidebarOpen}
          onClick={this.handleToggleSidebar}
        />
      </Flex>
    );
  };

  render() {
    const { $kaizen, match, loading: loadingStores } = this.props;
    const {
      selectedToolResults,
      isScrolledToBottom,
      isExistingChatLoading,
      searchInputValue,
      isTokenUsageOpen,
      isSidebarOpen
    } = this.state;

    const selectedJourney = $kaizen.chatSessionCollection?.selected;

    let title = 'New Conversation';
    if (selectedJourney) {
      title = selectedJourney.chatSessionTitle;
    }

    return (
      <Page pt={0} pb={0} px={0} title={title} scrolls={!this.hasAi} subnavTools={this.renderSubnavTools()}>
        <KaizenMainContent
          hasAi={this.hasAi}
          loadingStores={loadingStores}
          showSidebar={isSidebarOpen}
          isScrolledToBottom={isScrolledToBottom}
          isExistingChatLoading={isExistingChatLoading}
          inputRef={this.inputRef}
          scrollAreaRef={this.scrollAreaRef}
          selectedToolResults={selectedToolResults}
          searchInputValue={searchInputValue}
          onScroll={this.handleScroll}
          onScrollToBottom={this.scrollToBottom}
          onSelectToolResult={this.handleSelectToolResult}
          onCreateJourney={this.handleCreateJourney}
          onSelectJourneyAfterCreation={this.handleSelectJourneyAfterCreation}
          onCloseToolResult={this.handleCloseToolResult}
          onCloseToolResults={this.handleCloseToolResults}
          onSelectJourney={this.handleSelectJourney}
          onNewChat={this.handleEmptyStart}
          onToggleMyJourneys={this.handleToggleMyJourneys}
          onToggleCompanyJourneys={this.handleToggleCompanyJourneys}
          onSearchInputChange={this.handleSearchInputChange}
          onViewPrompt={this.handleViewPrompt}
        />
        {isTokenUsageOpen && (
          <TokenUsageDialog
            isOpen={isTokenUsageOpen}
            onClose={this.handleCloseTokenUsage}
            sessionId={match?.params?.sessionId}
          />
        )}
      </Page>
    );
  }
}

export default KaizenMain;
